import type { AgentGraphOutputs, AgentTool, TaskCatalogKey } from '@onefootprint/request-types/dashboard';
import type { Dispatch, RefObject } from 'react';
import { buildSkillsActions, type SkillsActions } from '@/modules/skill';
import { catalogParentId } from '../store/catalog-position';
import { inputsKeyError, takenOutputKeys } from '../store/field-key';
import type { Action, State } from '../store/reducer';
import { type BundleNode, isTaskNode, type ManagedNode } from '../store/types';
import { inputsToWire, managedNodeForWire } from '../store/wire';
import buildNodesActions, { type DecisionActions, type RulesActions, type TasksActions } from './build-nodes-actions';
import type { MutateOp, Restore } from './use-builder-mutation';
import useNodeClipboard, { withFreshArtifactKeys } from './use-node-clipboard';
export type BuilderActions = {
  addTask: (args?: {
    afterNodeId?: string;
    prepend?: boolean;
  }) => void;
  addRulesNode: (args?: {
    afterNodeId?: string;
    prepend?: boolean;
  }) => void;
  addDecisionNode: (args?: {
    afterNodeId?: string;
    prepend?: boolean;
  }) => void;
  addCatalogTask: (pick: {
    key: TaskCatalogKey;
    addInputs: string[];
    addTools: AgentTool[];
  }) => void;
  removeNode: (nodeId: string) => void;
  moveNode: (nodeId: string, direction: 'up' | 'down') => void;
  copyNode: (node: BundleNode) => Promise<void>;
  pasteNode: (args?: {
    afterNodeId?: string;
  }) => Promise<void>;
  copyInputs: () => Promise<void>;
  pasteInputs: () => Promise<void>;
  editNorms: (norms: string) => void;
  setTools: (tools: AgentTool[]) => void;
  updateManagedConfig: (node: ManagedNode, nextConfig: Record<string, unknown>) => void;
  makeManagedNodeCustom: (nodeId: string) => void;
  setOutputs: (outputs: AgentGraphOutputs) => void;
  unpickReports: (artifactIds: string[]) => void;
  startReport: (taskId: string, report: {
    name: string;
    spec: string;
    focusSectionId: string;
  }) => void;
  skills: SkillsActions;
  tasks: TasksActions;
  rules: RulesActions;
  decision: DecisionActions;
  undo: () => Promise<void>;
  redo: () => Promise<void>;
};
type Args = {
  stateRef: RefObject<State>;
  dispatch: Dispatch<Action>;
  mutateOp: MutateOp;
  restore: Restore;
};
const useBuilderActions = ({
  stateRef,
  dispatch,
  mutateOp,
  restore
}: Args): BuilderActions => {
  const skills_0 = buildSkillsActions({
    getState: () => ({
      skills: stateRef.current.bundle.skills,
      selectedPath: stateRef.current.selectedSkillPath
    }),
    apply: result => {
      dispatch({
        type: 'updateSkills',
        skills: result.skills
      });
      if (result.selectedPath !== undefined) dispatch({
        type: 'setSelectedSkillPath',
        path: result.selectedPath
      });
    },
    save: skills => mutateOp({
      kind: 'update_skills',
      skills
    }),
    select: path => dispatch({
      type: 'setSelectedSkillPath',
      path
    })
  });
  const nodes = buildNodesActions({
    stateRef,
    dispatch,
    mutateOp
  });
  const {
    copyNode,
    copyInputs,
    readClipboardNode,
    readClipboardInputs
  } = useNodeClipboard();
  const setOutputs = (outputs: AgentGraphOutputs) => {
    dispatch({
      type: 'updateOutputs',
      outputs
    });
    mutateOp({
      kind: 'update_outputs',
      outputs
    });
  };

  // Call before removing a report. Saved first, no version (an undo lands on the one in between) picks
  // a report that's gone, which blocks publishing.
  const unpickReports = (artifactIds: string[]) => {
    const outputs_0 = stateRef.current.bundle.outputs;
    if (outputs_0?.artifactKey && artifactIds.includes(outputs_0.artifactKey)) {
      setOutputs({
        ...outputs_0,
        artifactKey: undefined
      });
    }
  };
  const resolveParent = (afterNodeId: string | undefined, prepend: boolean | undefined): string | undefined => {
    if (prepend) return undefined;
    const list = stateRef.current.bundle.nodes;
    return afterNodeId ?? list[list.length - 1]?.id;
  };
  return {
    addTask: ({
      afterNodeId: afterNodeId_0,
      prepend: prepend_0
    } = {}) => {
      mutateOp({
        kind: 'add_node',
        nodeKind: 'task',
        parentNodeId: resolveParent(afterNodeId_0, prepend_0)
      });
    },
    addRulesNode: ({
      afterNodeId: afterNodeId_1,
      prepend: prepend_1
    } = {}) => {
      mutateOp({
        kind: 'add_node',
        nodeKind: 'rules',
        parentNodeId: resolveParent(afterNodeId_1, prepend_1)
      });
    },
    addDecisionNode: ({
      afterNodeId: afterNodeId_2,
      prepend: prepend_2
    } = {}) => {
      mutateOp({
        kind: 'add_node',
        nodeKind: 'decision',
        parentNodeId: resolveParent(afterNodeId_2, prepend_2)
      });
    },
    addCatalogTask: ({
      key,
      addInputs,
      addTools
    }) => {
      const parentNodeId = catalogParentId(stateRef.current.bundle.nodes);
      mutateOp({
        kind: 'add_catalog_task',
        key,
        addInputs,
        addTools,
        parentNodeId
      });
    },
    removeNode: nodeId => {
      const node = stateRef.current.bundle.nodes.find(candidate => candidate.id === nodeId);
      if (node && isTaskNode(node)) unpickReports(node.artifacts.map(artifact => artifact.id));
      mutateOp({
        kind: 'remove_node',
        nodeId
      });
    },
    moveNode: (nodeId_0, direction) => {
      mutateOp({
        kind: 'reorder_node',
        nodeId: nodeId_0,
        direction
      });
    },
    copyNode,
    pasteNode: async ({
      afterNodeId: afterNodeId_3
    } = {}) => {
      const node_0 = await readClipboardNode();
      if (!node_0) return;
      const taken = takenOutputKeys(stateRef.current.bundle);
      mutateOp({
        kind: 'paste_node',
        parentNodeId: afterNodeId_3,
        node: withFreshArtifactKeys(node_0, taken)
      });
    },
    copyInputs: () => copyInputs(stateRef.current.bundle.inputs),
    pasteInputs: async () => {
      const incoming = await readClipboardInputs();
      if (!incoming?.length) return;
      dispatch({
        type: 'updateInputs',
        inputs: incoming
      });
      if (inputsKeyError(incoming)) return;
      mutateOp({
        kind: 'update_inputs',
        inputs: inputsToWire(incoming)
      });
    },
    editNorms: (norms: string) => {
      dispatch({
        type: 'updateNorms',
        norms
      });
    },
    // Toggling a tool is a discrete action, so persist immediately (like adding/removing an
    // input) rather than deferring to a blur. The optimistic dispatch keeps the UI in sync while
    // the PATCH is in flight.
    setTools: (tools: AgentTool[]) => {
      dispatch({
        type: 'updateTools',
        tools
      });
      mutateOp({
        kind: 'update_tools',
        tools
      });
    },
    // Config edits ride the generic update_node op — there is no managed-specific op.
    updateManagedConfig: (node_1, nextConfig) => {
      // Every other save path skips a historical preview; this one writes immediately, so it must too.
      if (stateRef.current.current?.isHistoricalPreview) return;
      dispatch({
        type: 'updateManagedNode',
        id: node_1.id,
        config: nextConfig
      });
      mutateOp({
        kind: 'update_node',
        node: managedNodeForWire({
          ...node_1,
          config: nextConfig
        })
      });
    },
    setOutputs,
    unpickReports,
    // The agent's first report becomes the one investigations open, so the Output page has it to edit.
    startReport: (taskId, {
      name,
      spec,
      focusSectionId
    }) => {
      const {
        bundle
      } = stateRef.current;
      const isFirst = !bundle.nodes.some(node_2 => isTaskNode(node_2) && node_2.artifacts.length > 0);
      const key_0 = nodes.tasks.addArtifact(taskId, {
        name,
        spec
      });
      if (!key_0) return;
      dispatch({
        type: 'focusSection',
        id: focusSectionId
      });
      if (isFirst) setOutputs({
        ...bundle.outputs,
        artifactKey: key_0
      });
    },
    makeManagedNodeCustom: nodeId_1 => {
      mutateOp({
        kind: 'make_managed_node_custom',
        nodeId: nodeId_1
      });
    },
    skills: skills_0,
    tasks: nodes.tasks,
    rules: nodes.rules,
    decision: nodes.decision,
    undo: async () => {
      await restore('previous');
    },
    redo: async () => {
      await restore('next');
    }
  };
};
export default useBuilderActions;